import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import './App.css';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';

// Layout
import DashboardLayout from './components/layout/DashboardLayout';

// Admin Pages
import SuperAdminDashboard from './pages/admin/SuperAdminDashboard';

// Builder Pages
import BuilderDashboard from './pages/builder/BuilderDashboard';
import PartnersPage from './pages/builder/PartnersPage';
import BrandingPage from './pages/builder/BrandingPage';
import DomainSettingsPage from './pages/builder/DomainSettingsPage';

// Partner Pages
import PartnerDashboard from './pages/partner/PartnerDashboard';
import DomainManagementPage from './pages/partner/DomainManagementPage';

// Public Pages
import PublicMicrositePage from './pages/public/PublicMicrositePage';

// Shared Pages
import LeadsPage from './pages/shared/LeadsPage';
import ProjectsPage from './pages/shared/ProjectsPage';
import MicrositesPage from './pages/shared/MicrositesPage';
import AnalyticsPage from './pages/shared/AnalyticsPage';
import ClientTaggingPage from './pages/shared/ClientTaggingPage';
import SiteVisitsPage from './pages/shared/SiteVisitsPage';
import CommissionsPage from './pages/shared/CommissionsPage';
import InventoryPage from './pages/shared/InventoryPage';
import CollateralsPage from './pages/shared/CollateralsPage';
import TeamManagementPage from './pages/shared/TeamManagementPage';
import LeaderboardPage from './pages/shared/LeaderboardPage';
import PerformanceGoalsPage from './pages/shared/PerformanceGoalsPage';
import MicrositeBuilderPage from './pages/shared/MicrositeBuilderPage';
import CRMPage from './pages/shared/CRMPage';
import SubUsersPage from './pages/shared/SubUsersPage';
import SalesEnablementPage from './pages/shared/SalesEnablementPage';
import FloorPlansPage from './pages/shared/FloorPlansPage';
import FloorPlanMapper from './pages/shared/FloorPlanMapper';
import ProjectDetailPage from './pages/shared/ProjectDetailPage';
import InvoicesPage from './pages/shared/InvoicesPage';
import DemandLettersPage from './pages/shared/DemandLettersPage';
import SalesInventoryView from './pages/shared/SalesInventoryView';
import PerformancePage from './pages/shared/PerformancePage';
import CalendarPage from './pages/shared/CalendarPage';

// Buyer Portal Pages
import BuyerLayout from './pages/buyer/BuyerLayout';
import BuyerLoginPage from './pages/buyer/BuyerLoginPage';
import BuyerDashboard from './pages/buyer/BuyerDashboard';
import BuyerDocumentsPage from './pages/buyer/BuyerDocumentsPage';
import BuyerComplaintsPage from './pages/buyer/BuyerComplaintsPage';
import BuyerReferralsPage from './pages/buyer/BuyerReferralsPage';
import BuyerRentManagementPage from './pages/buyer/BuyerRentManagementPage';

// Admin Pages (additional)
import PlatformEmailSettingsPage from './pages/admin/PlatformEmailSettingsPage';

// Builder Pages (additional)
import BuilderEmailSettingsPage from './pages/builder/BuilderEmailSettingsPage';
import BuyersManagementPage from './pages/builder/BuyersManagementPage';

// Loading Spinner
const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="h-10 w-10 border-4 border-amber-600 border-t-transparent rounded-full animate-spin" />
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    const redirectPath = {
      'super_admin': '/admin',
      'builder_admin': '/builder',
      'sales_team': '/sales',
      'marketing_team': '/marketing',
      'partner': '/partner',
      'sub_user': '/partner',
    }[user?.role] || '/login';
    
    return <Navigate to={redirectPath} replace />;
  }

  return <DashboardLayout>{children}</DashboardLayout>;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    const redirectPath = {
      'super_admin': '/admin',
      'builder_admin': '/builder',
      'sales_team': '/sales',
      'marketing_team': '/marketing',
      'partner': '/partner',
      'sub_user': '/partner',
    }[user?.role] || '/';
    
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

// Placeholder for pages not yet fully implemented
const PlaceholderPage = ({ title }) => (
  <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
    <div className="h-16 w-16 bg-muted rounded-full flex items-center justify-center mb-4">
      <span className="text-2xl">🚧</span>
    </div>
    <h2 className="text-xl font-semibold mb-2">{title}</h2>
    <p className="text-muted-foreground">This page is under construction</p>
  </div>
);

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      
      {/* Public Microsite Route */}
      <Route path="/m/:slug" element={<PublicMicrositePage />} />
      <Route path="/site/:slug" element={<PublicMicrositePage />} />

      {/* Super Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/builders" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute allowedRoles={['super_admin']}><TeamManagementPage /></ProtectedRoute>} />
      <Route path="/admin/email-settings" element={<ProtectedRoute allowedRoles={['super_admin']}><PlatformEmailSettingsPage /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute allowedRoles={['super_admin']}><PlaceholderPage title="Platform Settings" /></ProtectedRoute>} />

      {/* Builder Admin Routes */}
      <Route path="/builder" element={<ProtectedRoute allowedRoles={['builder_admin']}><BuilderDashboard /></ProtectedRoute>} />
      <Route path="/builder/projects" element={<ProtectedRoute allowedRoles={['builder_admin']}><ProjectsPage /></ProtectedRoute>} />
      <Route path="/builder/projects/:id" element={<ProtectedRoute allowedRoles={['builder_admin']}><ProjectDetailPage /></ProtectedRoute>} />
      <Route path="/builder/partners" element={<ProtectedRoute allowedRoles={['builder_admin']}><PartnersPage /></ProtectedRoute>} />
      <Route path="/builder/leads" element={<ProtectedRoute allowedRoles={['builder_admin']}><LeadsPage /></ProtectedRoute>} />
      <Route path="/builder/tagging" element={<ProtectedRoute allowedRoles={['builder_admin']}><ClientTaggingPage /></ProtectedRoute>} />
      <Route path="/builder/site-visits" element={<ProtectedRoute allowedRoles={['builder_admin']}><SiteVisitsPage /></ProtectedRoute>} />
      <Route path="/builder/commissions" element={<ProtectedRoute allowedRoles={['builder_admin']}><CommissionsPage /></ProtectedRoute>} />
      <Route path="/builder/inventory" element={<ProtectedRoute allowedRoles={['builder_admin']}><InventoryPage /></ProtectedRoute>} />
      <Route path="/builder/floor-plans" element={<ProtectedRoute allowedRoles={['builder_admin']}><FloorPlansPage /></ProtectedRoute>} />
      <Route path="/builder/floor-plans/:id" element={<ProtectedRoute allowedRoles={['builder_admin']}><FloorPlanMapper /></ProtectedRoute>} />
      <Route path="/builder/microsites" element={<ProtectedRoute allowedRoles={['builder_admin']}><MicrositesPage /></ProtectedRoute>} />
      <Route path="/builder/collaterals" element={<ProtectedRoute allowedRoles={['builder_admin']}><CollateralsPage /></ProtectedRoute>} />
      <Route path="/builder/analytics" element={<ProtectedRoute allowedRoles={['builder_admin']}><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/builder/team" element={<ProtectedRoute allowedRoles={['builder_admin']}><TeamManagementPage /></ProtectedRoute>} />
      <Route path="/builder/sub-users" element={<ProtectedRoute allowedRoles={['builder_admin']}><SubUsersPage /></ProtectedRoute>} />
      <Route path="/builder/branding" element={<ProtectedRoute allowedRoles={['builder_admin']}><BrandingPage /></ProtectedRoute>} />
      <Route path="/builder/domain-settings" element={<ProtectedRoute allowedRoles={['builder_admin']}><DomainSettingsPage /></ProtectedRoute>} />
      <Route path="/builder/leaderboard" element={<ProtectedRoute allowedRoles={['builder_admin']}><LeaderboardPage /></ProtectedRoute>} />
      <Route path="/builder/goals" element={<ProtectedRoute allowedRoles={['builder_admin']}><PerformanceGoalsPage /></ProtectedRoute>} />
      <Route path="/builder/crm" element={<ProtectedRoute allowedRoles={['builder_admin']}><CRMPage /></ProtectedRoute>} />
      <Route path="/builder/sales-enablement" element={<ProtectedRoute allowedRoles={['builder_admin']}><SalesEnablementPage /></ProtectedRoute>} />
      <Route path="/builder/invoices" element={<ProtectedRoute allowedRoles={['builder_admin']}><InvoicesPage /></ProtectedRoute>} />
      <Route path="/builder/demand-letters" element={<ProtectedRoute allowedRoles={['builder_admin']}><DemandLettersPage /></ProtectedRoute>} />
      <Route path="/builder/performance" element={<ProtectedRoute allowedRoles={['builder_admin']}><PerformancePage /></ProtectedRoute>} />
      <Route path="/builder/email-settings" element={<ProtectedRoute allowedRoles={['builder_admin']}><BuilderEmailSettingsPage /></ProtectedRoute>} />
      <Route path="/builder/calendar" element={<ProtectedRoute allowedRoles={['builder_admin']}><CalendarPage /></ProtectedRoute>} />
      <Route path="/builder/buyers" element={<ProtectedRoute allowedRoles={['builder_admin']}><BuyersManagementPage /></ProtectedRoute>} />

      {/* Sales Team Routes */}
      <Route path="/sales" element={<ProtectedRoute allowedRoles={['sales_team']}><BuilderDashboard /></ProtectedRoute>} />
      <Route path="/sales/partners" element={<ProtectedRoute allowedRoles={['sales_team']}><PartnersPage /></ProtectedRoute>} />
      <Route path="/sales/tagging" element={<ProtectedRoute allowedRoles={['sales_team']}><ClientTaggingPage /></ProtectedRoute>} />
      <Route path="/sales/site-visits" element={<ProtectedRoute allowedRoles={['sales_team']}><SiteVisitsPage /></ProtectedRoute>} />
      <Route path="/sales/inventory" element={<ProtectedRoute allowedRoles={['sales_team']}><SalesInventoryView /></ProtectedRoute>} />
      <Route path="/sales/leads" element={<ProtectedRoute allowedRoles={['sales_team']}><LeadsPage /></ProtectedRoute>} />
      <Route path="/sales/crm" element={<ProtectedRoute allowedRoles={['sales_team']}><CRMPage /></ProtectedRoute>} />
      <Route path="/sales/performance" element={<ProtectedRoute allowedRoles={['sales_team']}><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/sales/leaderboard" element={<ProtectedRoute allowedRoles={['sales_team']}><LeaderboardPage /></ProtectedRoute>} />
      <Route path="/sales/goals" element={<ProtectedRoute allowedRoles={['sales_team']}><PerformanceGoalsPage /></ProtectedRoute>} />
      <Route path="/sales/calendar" element={<ProtectedRoute allowedRoles={['sales_team']}><CalendarPage /></ProtectedRoute>} />

      {/* Marketing Team Routes */}
      <Route path="/marketing" element={<ProtectedRoute allowedRoles={['marketing_team']}><BuilderDashboard /></ProtectedRoute>} />
      <Route path="/marketing/collaterals" element={<ProtectedRoute allowedRoles={['marketing_team']}><CollateralsPage /></ProtectedRoute>} />
      <Route path="/marketing/microsites" element={<ProtectedRoute allowedRoles={['marketing_team']}><MicrositesPage /></ProtectedRoute>} />
      <Route path="/marketing/sales-enablement" element={<ProtectedRoute allowedRoles={['marketing_team']}><SalesEnablementPage /></ProtectedRoute>} />
      <Route path="/marketing/templates" element={<ProtectedRoute allowedRoles={['marketing_team']}><PlaceholderPage title="Templates" /></ProtectedRoute>} />

      {/* Partner Routes */}
      <Route path="/partner" element={<ProtectedRoute allowedRoles={['partner', 'sub_user']}><PartnerDashboard /></ProtectedRoute>} />
      <Route path="/partner/projects" element={<ProtectedRoute allowedRoles={['partner', 'sub_user']}><ProjectsPage /></ProtectedRoute>} />
      <Route path="/partner/leads" element={<ProtectedRoute allowedRoles={['partner', 'sub_user']}><LeadsPage /></ProtectedRoute>} />
      <Route path="/partner/crm" element={<ProtectedRoute allowedRoles={['partner', 'sub_user']}><CRMPage /></ProtectedRoute>} />
      <Route path="/partner/tagging" element={<ProtectedRoute allowedRoles={['partner', 'sub_user']}><ClientTaggingPage /></ProtectedRoute>} />
      <Route path="/partner/site-visits" element={<ProtectedRoute allowedRoles={['partner', 'sub_user']}><SiteVisitsPage /></ProtectedRoute>} />
      <Route path="/partner/microsites" element={<ProtectedRoute allowedRoles={['partner', 'sub_user']}><MicrositesPage /></ProtectedRoute>} />
      <Route path="/partner/microsites/new" element={<ProtectedRoute allowedRoles={['partner', 'sub_user']}><MicrositeBuilderPage /></ProtectedRoute>} />
      <Route path="/partner/microsites/:id/edit" element={<ProtectedRoute allowedRoles={['partner', 'sub_user']}><MicrositeBuilderPage /></ProtectedRoute>} />
      <Route path="/partner/microsites/domains" element={<ProtectedRoute allowedRoles={['partner']}><DomainManagementPage /></ProtectedRoute>} />
      <Route path="/partner/floor-plans" element={<ProtectedRoute allowedRoles={['partner', 'sub_user']}><FloorPlansPage /></ProtectedRoute>} />
      <Route path="/partner/floor-plans/:id" element={<ProtectedRoute allowedRoles={['partner', 'sub_user']}><FloorPlanMapper /></ProtectedRoute>} />
      <Route path="/partner/projects" element={<ProtectedRoute allowedRoles={['partner', 'sub_user']}><ProjectsPage /></ProtectedRoute>} />
      <Route path="/partner/inventory" element={<ProtectedRoute allowedRoles={['partner', 'sub_user']}><SalesInventoryView /></ProtectedRoute>} />
      <Route path="/partner/commissions" element={<ProtectedRoute allowedRoles={['partner', 'sub_user']}><CommissionsPage /></ProtectedRoute>} />
      <Route path="/partner/invoices" element={<ProtectedRoute allowedRoles={['partner', 'sub_user']}><InvoicesPage /></ProtectedRoute>} />
      <Route path="/partner/calendar" element={<ProtectedRoute allowedRoles={['partner', 'sub_user']}><CalendarPage /></ProtectedRoute>} />
      <Route path="/partner/analytics" element={<ProtectedRoute allowedRoles={['partner', 'sub_user']}><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/partner/performance" element={<ProtectedRoute allowedRoles={['partner']}><PerformancePage /></ProtectedRoute>} />
      <Route path="/partner/team" element={<ProtectedRoute allowedRoles={['partner']}><TeamManagementPage /></ProtectedRoute>} />
      <Route path="/partner/sub-users" element={<ProtectedRoute allowedRoles={['partner']}><SubUsersPage /></ProtectedRoute>} />
      <Route path="/partner/leaderboard" element={<ProtectedRoute allowedRoles={['partner', 'sub_user']}><LeaderboardPage /></ProtectedRoute>} />
      <Route path="/partner/settings" element={<ProtectedRoute allowedRoles={['partner', 'sub_user']}><PlaceholderPage title="Settings" /></ProtectedRoute>} />

      {/* Buyer Portal Routes - Public/Separate Auth */}
      <Route path="/buyer/login" element={<BuyerLoginPage />} />
      <Route path="/buyer" element={<BuyerLayout />}>
        <Route index element={<BuyerDashboard />} />
        <Route path="documents" element={<BuyerDocumentsPage />} />
        <Route path="complaints" element={<BuyerComplaintsPage />} />
        <Route path="referrals" element={<BuyerReferralsPage />} />
        <Route path="rent" element={<BuyerRentManagementPage />} />
      </Route>

      {/* Root redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      
      {/* 404 */}
      <Route path="*" element={
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">404</h1>
            <p className="text-muted-foreground mb-4">Page not found</p>
            <a href="/login" className="text-amber-600 hover:underline">Go to Login</a>
          </div>
        </div>
      } />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
